// src/components/paginas/Login.jsx
// ====================================================================
// LOGIN ADMINISTRATIVO (separado del login del catálogo)
// ====================================================================

import React, { useState, useMemo, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, useLocation } from 'react-router-dom';

// Si no viene del .env, usamos el mismo origen donde corre el front
const API_BASE = process.env.REACT_APP_API_BASE || window.location.origin;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const HAS_RECAPTCHA = !!RECAPTCHA_SITE_KEY && !isLocalhost;

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = useMemo(
    () => location.state?.from?.pathname || '/admin',
    [location.state]
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Debug UI opcional
  const [debugOpen, setDebugOpen] = useState(false);
  const [lastResp, setLastResp] = useState(null);

  // Ref para ReCAPTCHA invisible
  const recaptchaRef = useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLastResp(null);

    let recaptchaToken = '';
    try {
      if (HAS_RECAPTCHA && recaptchaRef.current) {
        recaptchaToken = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset();
      }
    } catch {}

    const url = `${API_BASE}/admin/login`;

    try {
      console.log('[ADMIN LOGIN] → POST', url, {
        origin: window.location.origin,
        credentials: 'include',
        emailPreview: email?.trim(),
      });

      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'include', // imprescindible para cookie de sesión
        body: JSON.stringify({ email: email.trim(), password: password.trim(), recaptchaToken }),
      });

      const data = await resp.json().catch(() => ({}));
      setLastResp({ status: resp.status, ok: resp.ok, data });

      console.log('[ADMIN LOGIN] ← RESP', {
        status: resp.status,
        ok: resp.ok,
        data,
        setCookie: resp.headers?.get('set-cookie') || '(no visible from browser)',
      });

      if (!resp.ok || !data?.ok) {
        // Mensajes más claros según status
        if (resp.status === 401) {
          setError(data?.msg || data?.message || 'Credenciales incorrectas.');
        } else if (resp.status === 403) {
          setError(data?.msg || data?.message || 'No autorizado para el panel de administración.');
        } else {
          setError(data?.msg || data?.message || 'No se pudo iniciar sesión.');
        }
        return;
      }

      if (!data.user?.isAdmin) {
        setError('Tu cuenta no tiene permisos de administrador.');
        return;
      }

      const target = data.redirect || fromPath || '/admin';
      console.log('[ADMIN LOGIN] redirect →', target);
      navigate(target, { replace: true });
    } catch (err) {
      console.error('[ADMIN LOGIN] NETWORK ERROR:', err);
      setError('Error de red: ' + (err?.message || 'intenta de nuevo'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="w-full sm:w-96 p-8 bg-white shadow-lg rounded-lg">
        {HAS_RECAPTCHA && (
          <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={recaptchaRef} />
        )}
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/assets/logo.png"
            alt="Logo de la empresa"
            className="w-64 h-auto"
          />
        </div>

        <h2 className="text-3xl font-semibold mb-4 text-center">
          Iniciar Sesión (Admin)
        </h2>

        <form onSubmit={handleLogin} autoComplete="on">
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Correo Electrónico
            </label>
            <input
              type="email"
              id="email"
              value={email}
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="border p-2 rounded w-full mt-1 focus:ring-2 focus:ring-blue-500"
              placeholder="tu@promequi.com.gt"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Contraseña
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="border p-2 pr-10 rounded w-full mt-1 focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm mb-4" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            className={`bg-blue-600 text-white p-2 rounded w-full mt-4 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
            disabled={loading}
          >
            {loading ? 'Cargando…' : 'Iniciar sesión'}
          </button>
        </form>

        {/* Bloque de debug opcional */}
        <div className="mt-4">
          <button
            type="button"
            className="text-xs text-gray-500 underline"
            onClick={() => setDebugOpen(v => !v)}
          >
            {debugOpen ? 'Ocultar debug' : 'Mostrar debug'}
          </button>
          {debugOpen && (
            <pre className="mt-2 p-2 text-xs bg-gray-100 rounded overflow-auto max-h-48">
{JSON.stringify(
  {
    API_BASE,
    fromPath,
    lastResp,
  },
  null,
  2
)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
